import { gsap } from "../../animations/gsapSetup.ts";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./RealtimeHeartRate.module.css";

/* ── Mini ECG path (reuse pattern from HeartRateTimeline) ── */
function buildMiniEcg(baseY: number, amp = 1): string {
  const parts = [`M0,${baseY}`];
  for (let i = 0; i < 4; i++) {
    const r = Math.round(12 * amp);
    const p = Math.round(3 * amp);
    const s = Math.round(5 * amp);
    const tw = Math.round(4 * amp);
    parts.push(
      "c3,0 6,0 9,0",
      `c1,0 2,${-p} 4,${-p} c2,0 3,${p} 4,${p}`,
      "c1,0 2,0 3,0",
      `c0.3,0 0.8,${Math.round(s * 0.3)} 1,${Math.round(s * 0.3)}`,
      `c0.2,-0.5 0.8,${-(r + s * 0.3)} 1.2,${-(r + s * 0.3)}`,
      `c0.2,${Math.round(r * 0.05)} 1,${r + s} 1.5,${r + s}`,
      `c0.3,${-Math.round(s * 0.3)} 0.7,${-s} 1,${-s}`,
      "c2,0 4,0 6,0",
      `c2,0 4,${-tw} 6,${-tw} c2,0 4,${tw} 6,${tw}`,
      "c3,0 6,0 9,0",
    );
  }
  return parts.join(" ");
}

/* ── On-court player data ── */
interface OnCourtPlayer {
  name: string;
  bpm: number;
  zonePct: number; // 0-100 position in zone bar
  zoneColor: string;
  bleStatus: "connected" | "reconnecting" | "lost";
}

const ON_COURT: OnCourtPlayer[] = [
  { name: "T. Rozier", bpm: 152, zonePct: 72, zoneColor: "#ffcc00", bleStatus: "connected" },
  { name: "K. Ware", bpm: 144, zonePct: 62, zoneColor: "#ffcc00", bleStatus: "connected" },
  { name: "J. Jaquez Jr.", bpm: 150, zonePct: 70, zoneColor: "#ffcc00", bleStatus: "connected" },
  { name: "B. Adebayo", bpm: 158, zonePct: 78, zoneColor: "#ff3b30", bleStatus: "connected" },
  { name: "T. Herro", bpm: 148, zonePct: 68, zoneColor: "#ffcc00", bleStatus: "reconnecting" },
];

const BLE_CLASS: Record<string, string> = {
  connected: styles.bleConnected,
  reconnecting: styles.bleReconnecting,
  lost: styles.bleLost,
};

const ECG_PATH = buildMiniEcg(16, 0.8);

/* ── Signal bar strengths (bench proximity visualization) ── */
const SIGNAL_BARS = [4, 6, 8, 10, 12, 14];

export function RealtimeHeartRate() {
  const { t } = useI18n();

  const bleLabels: Record<string, string> = {
    connected: t.aiTeam.realtimeBleConnected,
    reconnecting: t.aiTeam.realtimeBleReconnecting,
    lost: t.aiTeam.realtimeBleLost,
  };
  const reducedMotion = useReducedMotion();

  const ref = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const rows = el.querySelectorAll("[data-hr-row]");
    gsap.set(rows, { x: -30, opacity: 0 });
    gsap.to(rows, {
      x: 0,
      opacity: 1,
      duration: 0.6,
      stagger: 0.1,
      ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 75%", toggleActions: "play none none reverse" },
    });

    /* BPM counter animation */
    const bpmEls = el.querySelectorAll("[data-bpm]");
    for (const bpmEl of bpmEls) {
      const target = Number((bpmEl as HTMLElement).dataset.bpm) || 0;
      const obj = { val: 0 };
      gsap.to(obj, {
        val: target,
        duration: 1.2,
        ease: "power2.out",
        snap: { val: 1 },
        onUpdate: () => {
          (bpmEl as HTMLElement).textContent = String(Math.round(obj.val));
        },
        scrollTrigger: { trigger: el, start: "top 70%", toggleActions: "play none none reverse" },
      });
    }

    /* Polar info */
    const polarInfo = el.querySelector("[data-polar-info]");
    if (polarInfo) {
      gsap.set(polarInfo, { y: 30, opacity: 0 });
      gsap.to(polarInfo, {
        y: 0,
        opacity: 1,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: { trigger: el, start: "top 60%", toggleActions: "play none none reverse" },
      });
    }
  }, []);

  return (
    <section ref={ref} className={styles.section}>
      <h2 className={styles.title}>{t.aiTeam.realtimeTitle}</h2>
      <p className={styles.desc}>{t.aiTeam.realtimeDesc}</p>

      <div className={styles.panel}>
        {ON_COURT.map((player) => (
          <div key={player.name} className={styles.playerRow} data-hr-row>
            <span className={styles.playerName}>{player.name}</span>
            <div>
              <span className={styles.bpm} data-bpm={player.bpm}>
                0
              </span>
              <span className={styles.bpmUnit}>BPM</span>
            </div>
            <div className={styles.zoneBar}>
              <div
                className={styles.zoneFill}
                style={{ width: `${player.zonePct}%`, background: player.zoneColor }}
              />
            </div>
            <div className={styles.ecgWrap}>
              <svg
                className={styles.ecgMini}
                viewBox="0 0 180 32"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path d={ECG_PATH} className={styles.ecgPath} />
              </svg>
            </div>
            <div className={styles.bleStatus}>
              <span className={`${styles.bleDot} ${BLE_CLASS[player.bleStatus]}`} />
              {bleLabels[player.bleStatus]}
            </div>
          </div>
        ))}
      </div>

      {/* Signal bars visualization */}
      <div className={styles.signalBars}>
        {SIGNAL_BARS.map((h, i) => (
          <div
            key={h}
            className={`${styles.signalBar} ${i < 5 ? styles.signalActive : ""}`}
            style={{ height: `${h}px` }}
          />
        ))}
      </div>

      {/* Polar BLE info card */}
      <div className={styles.polarInfo} data-polar-info>
        <h3 className={styles.polarTitle}>{t.aiTeam.polarTitle}</h3>
        <p className={styles.polarDesc}>{t.aiTeam.polarDesc}</p>
        <ul className={styles.polarFeatures}>
          {t.aiTeam.polarFeatures.map((feat) => (
            <li key={feat} className={styles.polarFeature}>
              {feat}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
